import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { Button, LoadingState } from "@printtat/ui";
import { apiJson } from "@/lib/apiFetch";

interface PreferencesResponse {
  marketingOptIn: boolean;
  cartReminderOptIn: boolean;
  reviewRequestOptIn: boolean;
  unsubscribeToken: string;
}

const fetchPreferences = (): Promise<PreferencesResponse> =>
  apiJson<PreferencesResponse>("/email/preferences");

const updatePreferences = (
  body: Partial<Omit<PreferencesResponse, "unsubscribeToken">>,
): Promise<PreferencesResponse> =>
  apiJson<PreferencesResponse>("/email/preferences", {
    method: "PATCH",
    body,
  });

const Toggle = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex w-full items-start justify-between rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:border-gray-300"
  >
    <div className="pr-4">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
    <span
      aria-hidden
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition ${
        checked ? "bg-black" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 translate-y-0.5 transform rounded-full bg-white shadow ring-0 transition ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </span>
  </button>
);

export const EmailPreferencesSettings = () => {
  const [prefs, setPrefs] = useState<PreferencesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    fetchPreferences()
      .then((data) => {
        setPrefs(data);
        setLoading(false);
      })
      .catch((err) => {
        setError((err as Error).message);
        setLoading(false);
      });
  }, []);

  const handleToggle = async (
    key: keyof Omit<PreferencesResponse, "unsubscribeToken">,
  ) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    setError(null);
    try {
      const updated = await updatePreferences({ [key]: next[key] });
      setPrefs(updated);
      setSavedAt(new Date());
    } catch (err) {
      setError((err as Error).message);
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading email preferences..." />;
  }

  if (error && !prefs) {
    return <p className="text-sm text-red-700">{error}</p>;
  }

  if (!prefs) return null;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="mb-5 flex items-center gap-2">
          <Mail className="h-4 w-4 text-gray-700" />
          <h2 className="text-sm font-semibold text-gray-900">
            Email preferences
          </h2>
        </div>

        <p className="mb-5 text-sm text-gray-500">
          Transactional emails (verification, password reset, order
          confirmations) are sent regardless of these preferences. The toggles
          below only affect non-essential reminders.
        </p>

        <div className="space-y-3">
          <Toggle
            label="Cart reminders"
            description="One email if you leave items in your cart for more than a day. At most once per week."
            checked={prefs.cartReminderOptIn}
            onChange={() => handleToggle("cartReminderOptIn")}
          />
          <Toggle
            label="Review requests"
            description="A nudge to leave a review a few days after your order arrives."
            checked={prefs.reviewRequestOptIn}
            onChange={() => handleToggle("reviewRequestOptIn")}
          />
          <Toggle
            label="Marketing & re-engagement"
            description="Occasional product updates and a quarterly note if we haven't seen you in a while."
            checked={prefs.marketingOptIn}
            onChange={() => handleToggle("marketingOptIn")}
          />
        </div>

        {saving && (
          <p className="mt-4 text-xs text-gray-500">Saving…</p>
        )}
        {!saving && savedAt && (
          <p className="mt-4 text-xs text-gray-500">
            Saved {savedAt.toLocaleTimeString()}.
          </p>
        )}
        {error && !saving && (
          <p className="mt-4 text-xs text-red-700">{error}</p>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          One-click unsubscribe
        </h3>
        <p className="mb-3 text-sm text-gray-500">
          Marketing and cart-reminder emails carry a one-click unsubscribe
          link in their footer. You can also unsubscribe from everything
          non-essential here:
        </p>
        <Button
          variant="outline"
          disabled={
            !prefs.cartReminderOptIn &&
            !prefs.reviewRequestOptIn &&
            !prefs.marketingOptIn
          }
          onClick={async () => {
            setSaving(true);
            try {
              const updated = await updatePreferences({
                cartReminderOptIn: false,
                reviewRequestOptIn: false,
                marketingOptIn: false,
              });
              setPrefs(updated);
              setSavedAt(new Date());
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setSaving(false);
            }
          }}
        >
          Unsubscribe from all non-essential emails
        </Button>
      </div>
    </div>
  );
};
